import React from 'react'
import { DashboardCell, DashboardLayout, ReportPillarWidgetData } from '../../../types/gotypes'

/**
 * Report Pillar widget (#1080, design/2026-08-24-report-pillar-widget.md).
 *
 * An executive-report pillar — a heading plus an ordered list of Gen AI cards
 * compiled from a manifest — used to be a nested `layout` cell. That is why the
 * generic Edit Cell modal could replace a whole column with one empty widget
 * (#1075), and why nothing durable linked a dashboard back to its manifest.
 *
 * THE WIDGET IS A CONTAINER, NOT A NEW CARD IMPLEMENTATION. It renders by
 * projecting its entries into the layout shape they already had — one row per
 * entry, one cell per row — and handing that to the ordinary layout renderer.
 * View mode, edit mode and the shared/logged-out view therefore all go through
 * exactly the same code as before, which is what makes the converted output
 * identical to the nested layout's.
 *
 * The projection also preserves widget ADDRESSES: entry k lands at inner
 * row k, cell 0, so the "-k-0" path suffix that sign-off pins and
 * review-changes keys quote keeps pointing at the same card.
 */

/** The synthetic layout a pillar renders through. Pure — see the note above. */
export function pillarEntriesToLayout(data: ReportPillarWidgetData | undefined): DashboardLayout {
  return { rows: (data?.entries || []).map(entry => ({ cells: [entry.cell] })) }
}

/** Writes one entry's cell back, preserving everything else about the pillar. */
export function replacePillarEntryCell(
  data: ReportPillarWidgetData,
  entryIndex: number,
  cell: DashboardCell,
): ReportPillarWidgetData {
  return {
    ...data,
    entries: data.entries.map((entry, i) => (i === entryIndex ? { ...entry, cell } : entry)),
  }
}

/** What a pillar holds, so Delete can name it before destroying it (#1075). */
export function summarisePillar(data: ReportPillarWidgetData | undefined): { widgetCount: number; heading?: string } {
  return {
    widgetCount: data?.entries?.length || 0,
    heading: data?.heading || undefined,
  }
}

/**
 * Sets the pillar's heading, and the heading of the title_hero entry that
 * actually renders it, in one move. Keeping the two in step is the whole reason
 * `heading` is allowed to duplicate the entry's value.
 */
export function setPillarHeading(data: ReportPillarWidgetData, heading: string): ReportPillarWidgetData {
  let headingWritten = false
  const entries = data.entries.map(entry => {
    if (headingWritten || entry.kind !== 'title_hero' || !entry.cell.title_hero_data) return entry
    headingWritten = true
    return {
      ...entry,
      cell: { ...entry.cell, title_hero_data: { ...entry.cell.title_hero_data, heading } },
    }
  })
  return { ...data, heading, entries }
}

/** Removes one entry. Order is the narrative, so the rest keep their order. */
export function removePillarEntry(data: ReportPillarWidgetData, entryIndex: number): ReportPillarWidgetData {
  return { ...data, entries: data.entries.filter((_, i) => i !== entryIndex) }
}

/**
 * Appends a Gen AI card. A hand-added entry gets NO `manifest_section_id` —
 * that absence is the record of "not from the manifest", which drift detection
 * later reports as an addition rather than an error.
 */
export function appendPillarAnalysisEntry(data: ReportPillarWidgetData, cell: DashboardCell): ReportPillarWidgetData {
  return { ...data, entries: [...data.entries, { kind: 'analysis', cell }] }
}

/** The label the remove confirmation uses for one entry. */
export function pillarEntryLabel(data: ReportPillarWidgetData, entryIndex: number): string {
  const entry = data.entries[entryIndex]
  if (!entry) return 'this card'
  if (entry.kind === 'title_hero') {
    return entry.cell.title_hero_data?.heading || 'the pillar heading'
  }
  return entry.cell.multi_table_analysis_data?.title || 'an untitled Gen AI card'
}

interface ReportPillarWidgetRendererProps {
  data?: ReportPillarWidgetData
  /** Renders the entries. Supplied by the caller so this component never has to
   *  import the layout renderer and create an import cycle it does not need. */
  children?: React.ReactNode
}

/**
 * View-mode shell. The entries themselves are rendered by the caller's layout
 * renderer (see DashboardCellComponent), so there is no second rendering path
 * to drift.
 */
export const ReportPillarWidgetRenderer: React.FC<ReportPillarWidgetRendererProps> = ({ data, children }) => {
  if (!data || !data.entries || data.entries.length === 0) {
    return <div>Report pillar is empty</div>
  }
  return <>{children}</>
}

export default ReportPillarWidgetRenderer
